/**
 * Unified "Filter issues" card sitting between the page header and the
 * exception table. Single surface for every way the user narrows the
 * report.
 *
 *   +- Filter issues -----------------------------+ Reset filters
 *   |  Narrow results by issue area, reason, or search term.
 *   |
 *   |  ISSUE AREA
 *   |  [tile] [tile] [tile]   (+ optional summary chips)
 *   |
 *   |  REASON
 *   |  (chip) (chip) (chip)   ← active parent's sub-cards as pills
 *   |
 *   |  ─────────────────────────────────────────────
 *   |
 *   |  FILTERS
 *   |  [search]   [multiselect] [select] [toggle]   [Open|Fixed]   metadata→
 *   +---------------------------------------------------------------+
 *
 * Empty sections are hidden:
 *   - Issue area    when no card strips render
 *   - Reason        when the active parent has no sub-cards
 *   - Filters       when there are no generic filters, tabs, filterToggle,
 *                   or panel metadata
 *
 * The whole card renders `null` when nothing has content (eg. an
 * exception with no strips, no filters, no toggle).
 */
import React, { useMemo } from 'react'
import {
  Box,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import IssuePanel from './IssuePanel'
import IssueCard from './IssueCard'
import ReasonChip from './ReasonChip'
import FilterStripRenderer from './FilterStripRenderer'
import FilterPanelHeader from './FilterPanelHeader'
import { findActiveSubCards } from './IssueCardStrip'
import {
  findSearchFilter,
  renderFilterControl,
  renderSearchInput,
} from './exceptionFilterControls'
import type {
  ExceptionConfig,
  ExceptionFilterDef,
  ExceptionFilterStripDef,
  IssueCardEntry,
} from '../../../config/exceptions/types'
import { poolwerxColors } from '../../../theme/colors'

export interface PanelSummaryChipsControl {
  activeKey: string | null
  onChipClick: (key: string) => void
  counts: Record<string, number>
  /**
   * Multi-select state (used when ``config.summaryChips.multiSelect`` is
   * true). Click semantics on the chip become "toggle membership"
   * instead of "replace active key". When this array is present,
   * ``activeKey`` is unused.
   */
  activeKeys?: string[]
}

interface DataViewFilterPanelProps {
  config: ExceptionConfig
  strips: ExceptionFilterStripDef[]
  response: Record<string, any> | null
  filterCounts: Record<string, number> | null
  searchParams: URLSearchParams
  setSearchParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void
  /** Panel-level metadata (e.g. SK-LS "Oldest open Xd · Latest report"). */
  panelMetadata?: ExceptionConfig['panelMetadata']
  /**
   * Summary-chips strip rendered alongside the issue-area tiles. Wired
   * by the route when `config.summaryChips` is set.
   */
  summaryChips?: ExceptionConfig['summaryChips']
  summaryChipsControl?: PanelSummaryChipsControl
  /** Filter-values state lifted to the route via `useExceptionFilterValues`. */
  filterValues: Record<string, any>
  updateFilterValue: (key: string, value: any) => void
  filterEnabled: boolean
  setFilterEnabled: (next: boolean) => void
  /** Hooked up to the route's reset handler. */
  onReset: () => void
  hasAnyActiveFilter: boolean
  /**
   * Server-driven search hook-up. When set, the search input reads its
   * value from `serverSearch.value` and writes via `serverSearch.onChange`
   * — the route owns debounce + URL sync.
   */
  serverSearch?: { value: string; onChange: (value: string) => void }
  /**
   * Row data backing dynamic-option filters. Multiselect filters declared
   * with `extractOptions` (instead of static `options`) resolve their
   * dropdown contents against this set. Pass the same row array the table
   * receives so the dropdown reflects what's actually present in the data.
   */
  rows?: Record<string, any>[]
}

const SECTION_LABEL_SX = {
  color: 'text.secondary',
  letterSpacing: '0.5px',
  fontWeight: 600,
  fontSize: '0.7rem',
  textTransform: 'uppercase' as const,
  lineHeight: 1.4,
  mb: 0.75,
}

const DataViewFilterPanel: React.FC<DataViewFilterPanelProps> = ({
  config,
  strips,
  response,
  filterCounts,
  searchParams,
  setSearchParams,
  panelMetadata,
  summaryChips,
  summaryChipsControl,
  filterValues,
  updateFilterValue,
  filterEnabled,
  setFilterEnabled,
  onReset,
  hasAnyActiveFilter,
  serverSearch,
  rows,
}) => {
  const cardStrips = strips.filter(
    (s): s is Extract<ExceptionFilterStripDef, { type: 'cards' }> => s.type === 'cards',
  )
  const tabStrips = strips.filter((s) => s.type === 'tabs')

  // Resolve the active parent's sub-cards by replaying each card strip's
  // build + active-key contract directly. Avoids the callback round-trip
  // through `IssueCardStrip` (which would re-fire on every render and
  // bounce setState).
  //
  // Strips that opt into multi-select (`getActiveSubKeys` +
  // `applySubSelection`) get `activeKeys[]` and toggle/clear handlers;
  // strips that don't fall back to the legacy single-select shape via
  // `applySelection`.
  type Reasons = {
    cards: IssueCardEntry[]
    activeKeys: string[]
    onToggle: (key: string) => void
    onClear: () => void
  }
  let reasons: Reasons | null = null
  for (const strip of cardStrips) {
    const cards = strip.buildCards(response, filterCounts)
    if (cards.length === 0) continue
    const activeKey = strip.getActiveKey(searchParams)
    const parent = findActiveSubCards(cards, activeKey, strip.isCardActive)
    if (!parent || !parent.subCards || parent.key == null) continue
    const parentKey = parent.key
    const multi = !!strip.getActiveSubKeys && !!strip.applySubSelection
    const activeKeys = multi
      ? strip.getActiveSubKeys!(searchParams)
      : activeKey && activeKey !== parentKey
        ? [activeKey]
        : []
    const commit = (nextParams: URLSearchParams) => {
      if (nextParams.toString() === searchParams.toString()) return
      nextParams.delete('page')
      setSearchParams(nextParams, { replace: false })
    }
    const onToggle = (key: string) => {
      if (multi) {
        const set = new Set(activeKeys)
        if (set.has(key)) set.delete(key)
        else set.add(key)
        commit(strip.applySubSelection!(searchParams, parentKey, Array.from(set)))
      } else {
        const nextKey = activeKey === key ? parentKey : key
        commit(strip.applySelection(searchParams, nextKey))
      }
    }
    const onClear = () => {
      if (multi) commit(strip.applySubSelection!(searchParams, parentKey, []))
      else commit(strip.applySelection(searchParams, parentKey))
    }
    reasons = {
      cards: parent.subCards.cards,
      activeKeys,
      onToggle,
      onClear,
    }
    break
  }

  const hasSummaryChips =
    !!summaryChips && !!summaryChipsControl && summaryChips.categories.length > 0
  // Per-config `panelMetadata` is bespoke (e.g. Skimmer's CSV-report
  // "Latest report" wording, COGS month context). The universal
  // "Oldest open" KPI is rendered by `OldestOpenChip` on the Results
  // toolbar — not the filter card — so the panel no longer fabricates
  // a freshness block of its own.
  const metadataNode = panelMetadata ? panelMetadata(response) : null

  // Resolve dynamic-option multiselects against the current row set so the
  // dropdown lists what's actually present. `DataViewTable` does the
  // same resolution on its predicate side; this mirrors it for the UI.
  const resolvedFilters = useMemo<ExceptionFilterDef[]>(() => {
    return (config.filters ?? []).map((f) => {
      if (f.type === 'multiselect' && f.extractOptions) {
        return { ...f, options: f.extractOptions(rows ?? []) }
      }
      return f
    })
  }, [config.filters, rows])

  const searchFilter = findSearchFilter(resolvedFilters)
  const nonSearchFilters = resolvedFilters.filter((f) => f.type !== 'search')

  const hasIssueAreaSection = cardStrips.length > 0 || hasSummaryChips
  const hasReasonSection = reasons != null && reasons.cards.length > 0
  const hasGenericFilters = !!searchFilter || nonSearchFilters.length > 0
  const hasFilterToggle = !!config.filterToggle
  const hasTabs = tabStrips.length > 0
  const hasFiltersSection =
    hasGenericFilters || hasFilterToggle || hasTabs || metadataNode != null

  if (!hasIssueAreaSection && !hasReasonSection && !hasFiltersSection) return null

  return (
    <IssuePanel>
      <FilterPanelHeader
        title="Filter issues"
        helperText="Narrow results by issue area, reason, or search term."
        onReset={onReset}
        hasAnyActiveFilter={hasAnyActiveFilter}
        tightBottom={
          !(hasIssueAreaSection || hasReasonSection || hasFiltersSection)
        }
      />

      {hasIssueAreaSection ? (
        <Box sx={{ mb: hasReasonSection ? 1.25 : 0 }}>
          <Typography sx={SECTION_LABEL_SX}>Issue area</Typography>
          <Stack
            direction="row"
            alignItems="flex-start"
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            {cardStrips.map((strip, i) => (
              <FilterStripRenderer
                key={`card-${i}`}
                strip={strip}
                response={response}
                filterCounts={filterCounts}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
              />
            ))}
            {hasSummaryChips && summaryChips && summaryChipsControl ? (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'stretch',
                  gap: 1,
                }}
              >
                {summaryChips.categories.map((category) => {
                  const count = summaryChipsControl.counts[category.countField] ?? 0
                  const isActive = summaryChipsControl.activeKeys
                    ? summaryChipsControl.activeKeys.includes(category.key)
                    : summaryChipsControl.activeKey === category.key
                  return (
                    <IssueCard
                      key={category.key}
                      label={category.label}
                      count={count}
                      active={isActive}
                      onClick={() => summaryChipsControl.onChipClick(category.key)}
                    />
                  )
                })}
              </Box>
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {hasReasonSection && reasons ? (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ gap: 0.75, mb: 0.75, minHeight: 24 }}
          >
            <Typography sx={{ ...SECTION_LABEL_SX, mb: 0 }}>Reason</Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', letterSpacing: 0 }}
            >
              ·{' '}
              {reasons.activeKeys.length === 0
                ? 'All reasons included'
                : `${reasons.activeKeys.length} reason${
                    reasons.activeKeys.length === 1 ? '' : 's'
                  } selected`}
            </Typography>
            {reasons.activeKeys.length > 0 ? (
              <>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', letterSpacing: 0 }}
                >
                  ·
                </Typography>
                <Typography
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={reasons.onClear}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                    color: poolwerxColors.teal,
                    fontWeight: 500,
                    letterSpacing: 0,
                    font: 'inherit',
                    fontSize: '0.75rem',
                    lineHeight: 1.66,
                    '&:hover': { textDecoration: 'underline' },
                    '&:focus-visible': {
                      outline: `2px solid ${poolwerxColors.teal}`,
                      outlineOffset: 2,
                      borderRadius: 2,
                    },
                  }}
                >
                  <CloseIcon
                    sx={{ fontSize: '0.875rem', verticalAlign: 'middle' }}
                  />
                  Clear
                </Typography>
              </>
            ) : null}
          </Stack>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {reasons.cards.map((sub, idx) => (
              <ReasonChip
                key={sub.key ?? `sub-${idx}`}
                label={sub.label}
                count={sub.count}
                active={!!sub.key && reasons!.activeKeys.includes(sub.key)}
                onClick={() => sub.key && reasons!.onToggle(sub.key)}
              />
            ))}
          </Stack>
        </Box>
      ) : null}

      {hasFiltersSection ? (
        <>
          {(hasIssueAreaSection || hasReasonSection) ? (
            <Divider
              sx={{ mt: 1.25, mb: 1.25, borderColor: 'rgba(0, 0, 0, 0.06)' }}
            />
          ) : null}
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              sx={{ flexWrap: 'wrap', gap: 1.5, rowGap: 1.5 }}
            >
              {searchFilter ? (
                <Box sx={{ minWidth: 320, flex: '0 1 360px' }}>
                  {renderSearchInput({
                    filter: searchFilter,
                    value: serverSearch
                      ? serverSearch.value
                      : ((filterValues[searchFilter.key] ?? '') as string),
                    onChange: (next) => {
                      if (serverSearch) serverSearch.onChange(next)
                      else updateFilterValue(searchFilter.key, next)
                    },
                  })}
                </Box>
              ) : null}
              {nonSearchFilters.map((f) =>
                renderFilterControl({
                  filter: f,
                  value:
                    f.type === 'select'
                      ? (filterValues[f.key] ?? f.defaultValue ?? 'all')
                      : f.type === 'toggle'
                        ? !!(filterValues[f.key] ?? f.defaultEnabled)
                        : f.type === 'daterange'
                          ? (filterValues[f.key] ?? f.defaultValue ?? ['', ''])
                          : filterValues[f.key] ?? (f.type === 'multiselect' ? [] : ''),
                  onChange: (next) => updateFilterValue(f.key, next),
                }),
              )}
              {hasFilterToggle && config.filterToggle ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: poolwerxColors.tealLight,
                    borderRadius: 1,
                    px: 1.25,
                    py: 0.25,
                  }}
                >
                  {renderFilterControl({
                    filter: {
                      key: '__filter_toggle__',
                      type: 'toggle',
                      label: config.filterToggle.label,
                      defaultEnabled: config.filterToggle.defaultEnabled,
                      // predicate isn't called here — the table applies
                      // config.filterToggle directly when filterEnabled is
                      // true. This stub just satisfies the type.
                      predicate: () => true,
                    },
                    value: filterEnabled,
                    onChange: (next) => setFilterEnabled(!!next),
                  })}
                </Box>
              ) : null}
              {hasTabs ? (
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  {tabStrips.map((strip, i) => (
                    <FilterStripRenderer
                      key={`tab-${i}`}
                      strip={strip}
                      response={response}
                      filterCounts={filterCounts}
                      searchParams={searchParams}
                      setSearchParams={setSearchParams}
                    />
                  ))}
                </Stack>
              ) : null}
              {metadataNode != null ? (
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography
                    component="div"
                    variant="caption"
                    sx={{ color: 'text.secondary', textAlign: 'right' }}
                  >
                    {metadataNode}
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </Box>
        </>
      ) : null}
    </IssuePanel>
  )
}

export default DataViewFilterPanel
